import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Scale, Users, Receipt, Loader2, Share2, Pencil, QrCode, Link2, Check, X, MessageCircle, Trash2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

const eur = (v) => `€ ${(v ?? 0).toFixed(2)}`;

const payUrl = (link, amount) => {
  const clean = link.replace(/\/+$/, "");
  if (/paypal\.me\//i.test(clean)) return `${clean}/${amount.toFixed(2)}EUR`;
  if (/bunq\.me\//i.test(clean)) return `${clean}/${amount.toFixed(2)}`;
  return clean;
};

const epcPayload = (name, iban, amount, remittance) =>
  ["BCD", "002", "1", "SCT", "", name.slice(0, 70), iban, `EUR${amount.toFixed(2)}`, "", "", remittance.slice(0, 140), ""].join("\n");

export default function WhoPaysWhatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [split, setSplit] = useState(null);
  const [error, setError] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [payForm, setPayForm] = useState({ iban: "", pay_link: "" });
  const [savingPay, setSavingPay] = useState(false);
  const [qrFor, setQrFor] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/trips/${id}`), api.get(`/trips/${id}/split`)])
      .then(([t, s]) => { setTrip(t.data); setSplit(s.data); })
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-sm text-muted-foreground">Kon het kostenoverzicht niet laden.</p>
          <button data-testid="back-to-trip" onClick={() => navigate(`/trip/${id}`)} className="mt-4 text-sm font-bold text-primary">← Terug naar trip</button>
        </main>
      </div>
    );
  }
  if (!trip || !split) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <main className="max-w-3xl mx-auto px-4 py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></main>
      </div>
    );
  }

  const members = split.members || [];
  const perMember = split.per_member || {};
  const rows = members.map((m) => ({ name: m, ...(perMember[m] || { paid: 0, share: 0, net: 0 }) }))
    .sort((a, b) => b.paid - a.paid);
  const chartData = rows.map((r) => ({ naam: r.name, Uitgegeven: r.paid, Aandeel: r.share }));
  const settlements = split.settlements || [];
  const paymentInfo = split.payment_info || {};
  const repayments = (split.expenses || []).filter((e) => e.source === "settlement");
  const regularExpenses = (split.expenses || []).filter((e) => e.source !== "settlement");
  const hasData = members.length > 0 && regularExpenses.length > 0;

  const startEditPay = (name) => {
    const info = paymentInfo[name] || {};
    setPayForm({ iban: info.iban || "", pay_link: info.pay_link || "" });
    setEditMember(editMember === name ? null : name);
  };

  const savePayInfo = async () => {
    setSavingPay(true);
    try {
      const { data } = await api.put(`/trips/${id}/split/payment-info`, {
        name: editMember, iban: payForm.iban.trim() || null, pay_link: payForm.pay_link.trim() || null,
      });
      setSplit(data);
      setEditMember(null);
      toast.success("Betaalgegevens opgeslagen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPay(false);
    }
  };

  const requestWhatsApp = (s) => {
    const info = paymentInfo[s.to] || {};
    const lines = [`Hé ${s.from}, je betaalt nog € ${s.amount.toFixed(2)} aan ${s.to} voor "${trip.name}".`];
    if (info.pay_link) lines.push(`Betaal in één tik: ${payUrl(info.pay_link, s.amount)}`);
    if (info.iban) lines.push(`Of via overschrijving: ${info.iban} (mededeling: ${trip.name} — afrekening)`);
    lines.push("— via VoltRoute ⚡");
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
  };

  const undoSettle = async (expId) => {
    try {
      const { data } = await api.delete(`/trips/${id}/split/expenses/${expId}`);
      setSplit(data);
      toast.success("Terugbetaling ongedaan gemaakt");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markPaid = async (s) => {
    try {
      const { data } = await api.post(`/trips/${id}/split/settle`, { from_name: s.from, to_name: s.to, amount_eur: s.amount });
      const expId = data.settled_expense_id;
      setSplit(data);
      toast.success(`${s.from} betaalde € ${s.amount.toFixed(2)} terug aan ${s.to}`, {
        duration: 8000,
        action: { label: "Ongedaan maken", onClick: () => undoSettle(expId) },
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shareWhatsApp = () => {
    const lines = [`💶 Eindafrekening — ${trip.name}`, `Totaal verdeeld: € ${(split.total || 0).toFixed(2)}`, "", "Per persoon:"];
    rows.forEach((r) => {
      const status = r.net > 0.005 ? `krijgt € ${r.net.toFixed(2)} terug` : r.net < -0.005 ? `betaalt nog € ${Math.abs(r.net).toFixed(2)}` : "staat gelijk";
      lines.push(`• ${r.name}: € ${r.paid.toFixed(2)} voorgeschoten · aandeel € ${r.share.toFixed(2)} → ${status}`);
    });
    if (settlements.length > 0) {
      lines.push("", "Zo staan we weer gelijk:");
      settlements.forEach((s) => lines.push(`• ${s.from} betaalt € ${s.amount.toFixed(2)} aan ${s.to}`));
    } else {
      lines.push("", "Iedereen staat gelijk — niets te verrekenen 🎉");
    }
    lines.push("", "— gedeeld via VoltRoute ⚡");
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="who-pays-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <button data-testid="back-to-trip" onClick={() => navigate(`/trip/${id}`)}
          className="text-xs font-bold text-muted-foreground hover:text-foreground flex items-center gap-1 transition-[color] duration-200">
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar {trip.name}
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-1">Kostenverdeling</p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight flex items-center gap-2.5">
              <Scale className="w-7 h-7 text-[#3A6EA5]" /> Wie betaalt wat
            </h1>
          </div>
          {hasData && (
            <button data-testid="who-pays-share-whatsapp" onClick={shareWhatsApp}
              className="rounded-full bg-[#25D366] hover:bg-[#1fb457] text-white text-xs font-bold px-5 py-2.5 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
              <Share2 className="w-3.5 h-3.5" /> Deel afrekening via WhatsApp
            </button>
          )}
        </div>

        {!hasData ? (
          <div className="bg-card rounded-2xl p-8 text-center" data-testid="who-pays-empty">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nog geen verdeelde uitgaven voor deze trip.</p>
            <p className="text-xs text-muted-foreground mt-1">Voeg reisgenoten en uitgaven toe via het Budget-tabblad ("Kosten verdelen").</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-primary rounded-2xl p-4 text-primary-foreground">
                <p className="text-[10px] uppercase tracking-wide font-semibold opacity-80">Totaal verdeeld</p>
                <p data-testid="who-pays-total" className="font-mono font-extrabold text-xl mt-0.5">{eur(split.total)}</p>
              </div>
              <div className="bg-card rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Deelnemers</p>
                <p className="font-mono font-extrabold text-xl mt-0.5">{members.length}</p>
              </div>
              <div className="bg-card rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Uitgaven</p>
                <p className="font-mono font-extrabold text-xl mt-0.5">{regularExpenses.length}</p>
              </div>
            </div>

            <div className="bg-card rounded-2xl p-5 overflow-x-auto">
              <p className="font-heading font-bold text-sm mb-3">Per deelnemer</p>
              <table className="w-full text-sm" data-testid="who-pays-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="pb-2 font-semibold">Deelnemer</th>
                    <th className="pb-2 font-semibold text-right">Uitgegeven</th>
                    <th className="pb-2 font-semibold text-right">Aandeel</th>
                    <th className="pb-2 font-semibold text-right">Netto</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const info = paymentInfo[r.name] || {};
                    return (
                      <React.Fragment key={r.name}>
                        <tr data-testid={`who-pays-row-${r.name}`} className="border-b border-border/60 last:border-0">
                          <td className="py-2.5 font-semibold">
                            <span className="flex items-center gap-1.5">
                              {r.name}
                              {info.iban && <QrCode className="w-3 h-3 text-[#3A6EA5]" title="IBAN ingesteld" />}
                              {info.pay_link && <Link2 className="w-3 h-3 text-[#3A6EA5]" title="Betaallink ingesteld" />}
                              <button data-testid={`pay-info-edit-${r.name}`} onClick={() => startEditPay(r.name)} title="IBAN of betaallink instellen"
                                className={`p-1 rounded-full transition-[background-color] duration-200 ${editMember === r.name ? "bg-primary/10 text-primary" : "hover:bg-secondary text-muted-foreground"}`}>
                                <Pencil className="w-3 h-3" />
                              </button>
                            </span>
                          </td>
                          <td className="py-2.5 text-right font-mono">{eur(r.paid)}</td>
                          <td className="py-2.5 text-right font-mono text-muted-foreground">{eur(r.share)}</td>
                          <td className="py-2.5 text-right font-mono font-bold">
                            {r.net > 0.005 ? <span className="text-[#2D6A4F]">krijgt {eur(r.net)}</span>
                              : r.net < -0.005 ? <span className="text-red-600">betaalt {eur(Math.abs(r.net))}</span>
                              : <span className="text-muted-foreground font-normal">staat gelijk</span>}
                          </td>
                        </tr>
                        {editMember === r.name && (
                          <tr>
                            <td colSpan={4} className="pb-3">
                              <div className="bg-secondary/50 rounded-xl p-3 flex flex-col sm:flex-row gap-2 sm:items-end" data-testid={`pay-info-form-${r.name}`}>
                                <label className="text-[11px] text-muted-foreground flex-1">IBAN van {r.name}
                                  <input data-testid={`pay-info-iban-${r.name}`} value={payForm.iban} placeholder="BE68 5390 0754 7034"
                                    onChange={(e) => setPayForm((f) => ({ ...f, iban: e.target.value }))}
                                    className="w-full mt-1 bg-card rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary" />
                                </label>
                                <label className="text-[11px] text-muted-foreground flex-1">Betaallink (PayPal.me, bunq.me, Revolut...)
                                  <input data-testid={`pay-info-link-${r.name}`} value={payForm.pay_link} placeholder="paypal.me/naam"
                                    onChange={(e) => setPayForm((f) => ({ ...f, pay_link: e.target.value }))}
                                    className="w-full mt-1 bg-card rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
                                </label>
                                <span className="flex gap-1.5">
                                  <button data-testid={`pay-info-save-${r.name}`} onClick={savePayInfo} disabled={savingPay}
                                    className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-xs font-bold py-2 px-4 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
                                    <Check className="w-3.5 h-3.5" /> {savingPay ? "..." : "Opslaan"}
                                  </button>
                                  <button data-testid={`pay-info-cancel-${r.name}`} onClick={() => setEditMember(null)}
                                    className="rounded-full bg-card hover:bg-secondary text-xs font-bold py-2 px-3 transition-[background-color] duration-200">
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-card rounded-2xl p-5" data-testid="who-pays-settlements">
              <p className="font-heading font-bold text-sm mb-1">Zo staan jullie weer gelijk</p>
              {settlements.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="settlements-even">Iedereen staat gelijk — niets te verrekenen.</p>
              ) : (
                <div className="space-y-1 mt-2">
                  {settlements.map((s, i) => {
                    const info = paymentInfo[s.to] || {};
                    return (
                      <div key={i} data-testid={`settlement-${i}`} className="flex flex-wrap items-center gap-2 text-sm bg-secondary/60 rounded-xl px-3.5 py-2.5">
                        <span className="font-semibold">{s.from}</span>
                        <span className="text-muted-foreground text-xs flex items-center gap-1">betaalt <span className="font-mono font-bold text-foreground">{eur(s.amount)}</span> aan</span>
                        <ArrowRight className="w-3.5 h-3.5 text-[#3A6EA5]" />
                        <span className="font-semibold">{s.to}</span>
                        <span className="ml-auto flex items-center gap-1.5">
                          {info.iban && (
                            <button data-testid={`settlement-qr-${i}`} onClick={() => setQrFor(s)}
                              className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-[11px] font-bold px-3 py-1.5 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
                              <QrCode className="w-3 h-3" /> Betaal-QR
                            </button>
                          )}
                          {info.pay_link && (
                            <a data-testid={`settlement-paylink-${i}`} href={payUrl(info.pay_link, s.amount)} target="_blank" rel="noopener noreferrer"
                              className="rounded-full bg-[#3A6EA5] hover:bg-[#2a5580] text-white text-[11px] font-bold px-3 py-1.5 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
                              <Link2 className="w-3 h-3" /> Betaal {eur(s.amount)}
                            </a>
                          )}
                          {!info.iban && !info.pay_link && (
                            <button data-testid={`settlement-no-payinfo-${i}`} onClick={() => startEditPay(s.to)}
                              className="text-[10px] text-muted-foreground italic hover:text-foreground transition-[color] duration-200">
                              + IBAN/betaallink van {s.to}
                            </button>
                          )}
                          <button data-testid={`settlement-whatsapp-${i}`} onClick={() => requestWhatsApp(s)} title="Stuur betaalverzoek via WhatsApp"
                            className="rounded-full bg-[#25D366] hover:bg-[#1fb457] text-white p-1.5 active:scale-95 transition-[transform,background-color] duration-200">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`settlement-paid-${i}`} onClick={() => markPaid(s)} title="Markeer als betaald"
                            className="rounded-full border border-[#2D6A4F]/40 hover:bg-[#2D6A4F] hover:text-white text-[#2D6A4F] text-[11px] font-bold px-3 py-1.5 flex items-center gap-1 active:scale-95 transition-[transform,background-color,color] duration-200">
                            <Check className="w-3 h-3" /> Betaald
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {repayments.length > 0 && (
              <div className="bg-card rounded-2xl p-5" data-testid="repayments-log">
                <p className="font-heading font-bold text-sm mb-1 flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2D6A4F]" /> Terugbetaald</p>
                <div className="space-y-1 mt-2">
                  {repayments.map((e) => (
                    <div key={e.id} data-testid={`repayment-${e.id}`} className="flex items-center gap-2 text-sm bg-[#95D5B2]/15 rounded-xl px-3.5 py-2">
                      <Check className="w-3.5 h-3.5 text-[#2D6A4F] shrink-0" />
                      <span className="min-w-0 truncate">{e.description}{e.date ? <span className="text-xs text-muted-foreground"> · {e.date}</span> : null}</span>
                      <span className="ml-auto flex items-center gap-1.5 shrink-0">
                        <span className="font-mono font-bold">{eur(e.amount_eur)}</span>
                        <button data-testid={`repayment-undo-${e.id}`} onClick={() => undoSettle(e.id)} title="Terugbetaling ongedaan maken"
                          className="p-1.5 rounded-full hover:bg-red-50 text-red-400 transition-[background-color] duration-200">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-card rounded-2xl p-5" data-testid="who-pays-chart">
              <p className="font-heading font-bold text-sm mb-3 flex items-center gap-1.5"><Receipt className="w-4 h-4 text-[#3A6EA5]" /> Uitgaven per persoon</p>
              <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 40 + 80)}>
                <BarChart data={chartData} margin={{ top: 4, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="naam" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} width={56} />
                  <RTooltip formatter={(v, name) => [eur(Number(v)), name]} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Uitgegeven" fill="#2D6A4F" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Aandeel" fill="#3A6EA5" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-muted-foreground mt-1">Groen = wat iemand voorschoot · blauw = wat diegene eigenlijk moet dragen. Verschil = netto in de tabel.</p>
            </div>
          </>
        )}
      </main>
      <Dialog open={!!qrFor} onOpenChange={(o) => !o && setQrFor(null)}>
        <DialogContent data-testid="pay-qr-dialog" className="rounded-2xl max-w-sm">
          {qrFor && (() => {
            const info = paymentInfo[qrFor.to] || {};
            const remittance = `${trip.name} — afrekening`;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading">Betaal {eur(qrFor.amount)} aan {qrFor.to}</DialogTitle>
                  <DialogDescription>Scan met je bank-app (KBC, Belfius, ING, Argenta, Rabobank...) — bedrag en mededeling staan al klaar.</DialogDescription>
                </DialogHeader>
                <div className="flex justify-center py-2">
                  <div className="bg-white rounded-2xl p-4 shadow-inner">
                    <QRCodeSVG data-testid="pay-qr-code" value={epcPayload(qrFor.to, info.iban, qrFor.amount, remittance)} size={210} level="M" />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><span className="font-semibold text-foreground">Ontvanger:</span> {qrFor.to}</p>
                  <p className="font-mono"><span className="font-semibold font-sans text-foreground">IBAN:</span> {info.iban}</p>
                  <p><span className="font-semibold text-foreground">Mededeling:</span> {remittance}</p>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
